import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useNotifications } from '@/contexts/NotificationContext';
import { 
  requestNotificationPermission, 
  isNotificationSupported,
  registerServiceWorker 
} from '@/services/pushNotificationService';
import { toast } from 'sonner';
import { Bell, Mail, MessageSquare, Send } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const NotificationSettings = () => {
  const { preferences, isLoading, updatePreferences, sendTestNotification } = useNotifications();
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');
  const [isSaving, setIsSaving] = useState(false);
  const [testNotificationType, setTestNotificationType] = useState<'project_assignment' | 'task_assignment' | 'comment' | 'mention' | 'deadline_reminder'>('task_assignment');
  const [localPreferences, setLocalPreferences] = useState({
    emailEnabled: true,
    slackEnabled: false,
    pushEnabled: true,
    emailProjectAssignment: true,
    emailTaskAssignment: true,
    emailComments: true,
    emailDeadlines: true,
    emailMentions: true,
    slackWebhookUrl: '',
  });

  useEffect(() => {
    if (preferences) {
      setLocalPreferences({
        emailEnabled: preferences.emailEnabled,
        slackEnabled: preferences.slackEnabled,
        pushEnabled: preferences.pushEnabled,
        emailProjectAssignment: preferences.emailProjectAssignment,
        emailTaskAssignment: preferences.emailTaskAssignment,
        emailComments: preferences.emailComments,
        emailDeadlines: preferences.emailDeadlines,
        emailMentions: preferences.emailMentions,
        slackWebhookUrl: preferences.slackWebhookUrl || '',
      });
    }
  }, [preferences]);

  useEffect(() => {
    if (isNotificationSupported()) {
      setPushPermission(Notification.permission);
    }
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updatePreferences(localPreferences);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestPushPermission = async () => {
    if (!isNotificationSupported()) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    const permission = await requestNotificationPermission();
    setPushPermission(permission);

    if (permission === 'granted') {
      await registerServiceWorker();
      toast.success('Push notifications enabled!');
      await updatePreferences({ pushEnabled: true });
    } else if (permission === 'denied') {
      toast.error('Push notifications were denied. Please enable them in your browser settings.');
    }
  };

  const handleTestNotification = async (type: 'project_assignment' | 'task_assignment' | 'comment' | 'mention' | 'deadline_reminder') => {
    await sendTestNotification(type);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading notification preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Channels</CardTitle>
          <CardDescription>Enable or disable notification channels</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Label>Email Notifications</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Receive email updates about your projects
              </p>
            </div>
            <Switch
              checked={localPreferences.emailEnabled}
              onCheckedChange={(checked) =>
                setLocalPreferences({ ...localPreferences, emailEnabled: checked })
              }
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <Label>Slack Notifications</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Send notifications to Slack via webhook
              </p>
            </div>
            <Switch
              checked={localPreferences.slackEnabled}
              onCheckedChange={(checked) =>
                setLocalPreferences({ ...localPreferences, slackEnabled: checked })
              }
            />
          </div>
          {localPreferences.slackEnabled && (
            <div className="space-y-2 pt-2">
              <Label htmlFor="slackWebhook">Slack Webhook URL</Label>
              <Input
                id="slackWebhook"
                type="url"
                placeholder="https://hooks.slack.com/services/..."
                value={localPreferences.slackWebhookUrl}
                onChange={(e) =>
                  setLocalPreferences({ ...localPreferences, slackWebhookUrl: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use default webhook from environment
              </p>
            </div>
          )}
          <Separator />
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                <Label>Push Notifications</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Receive browser push notifications
              </p>
            </div>
            <div className="flex items-center gap-2">
              {pushPermission === 'granted' ? (
                <span className="text-xs text-green-600 dark:text-green-400">Enabled</span>
              ) : pushPermission === 'denied' ? (
                <span className="text-xs text-red-600 dark:text-red-400">Denied</span>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRequestPushPermission}
                >
                  Enable
                </Button>
              )}
              <Switch
                checked={localPreferences.pushEnabled && pushPermission === 'granted'}
                disabled={pushPermission !== 'granted'}
                onCheckedChange={(checked) =>
                  setLocalPreferences({ ...localPreferences, pushEnabled: checked })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Preferences */}
      {localPreferences.emailEnabled && (
        <Card>
          <CardHeader>
            <CardTitle>Email Preferences</CardTitle>
            <CardDescription>Choose which events trigger email notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Project Assignments</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when assigned to a project
                </p>
              </div>
              <Switch
                checked={localPreferences.emailProjectAssignment}
                onCheckedChange={(checked) =>
                  setLocalPreferences({ ...localPreferences, emailProjectAssignment: checked })
                }
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Task Assignments</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when assigned to a task
                </p>
              </div>
              <Switch
                checked={localPreferences.emailTaskAssignment}
                onCheckedChange={(checked) =>
                  setLocalPreferences({ ...localPreferences, emailTaskAssignment: checked })
                }
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Comments</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when someone comments on your projects or tasks
                </p>
              </div>
              <Switch
                checked={localPreferences.emailComments}
                onCheckedChange={(checked) =>
                  setLocalPreferences({ ...localPreferences, emailComments: checked })
                }
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Mentions</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when someone mentions you in a comment
                </p>
              </div>
              <Switch
                checked={localPreferences.emailMentions}
                onCheckedChange={(checked) =>
                  setLocalPreferences({ ...localPreferences, emailMentions: checked })
                }
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Deadline Reminders</Label>
                <p className="text-sm text-muted-foreground">
                  Receive reminders about upcoming deadlines
                </p>
              </div>
              <Switch
                checked={localPreferences.emailDeadlines}
                onCheckedChange={(checked) =>
                  setLocalPreferences({ ...localPreferences, emailDeadlines: checked })
                }
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Notifications */}
      <Card>
        <CardHeader>
          <CardTitle>Test Notifications</CardTitle>
          <CardDescription>Send test notifications to verify your settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Notification Type</Label>
            <Select
              value={testNotificationType}
              onValueChange={(value: 'project_assignment' | 'task_assignment' | 'comment' | 'mention' | 'deadline_reminder') => {
                setTestNotificationType(value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select notification type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="project_assignment">Project Assignment</SelectItem>
                <SelectItem value="task_assignment">Task Assignment</SelectItem>
                <SelectItem value="comment">Comment</SelectItem>
                <SelectItem value="mention">Mention</SelectItem>
                <SelectItem value="deadline_reminder">Deadline Reminder</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => handleTestNotification(testNotificationType)}
            className="w-full"
            variant="outline"
          >
            <Send className="h-4 w-4 mr-2" />
            Send Test Notification
          </Button>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
};

